/**
 * TeachBud Assessment Generator
 *
 * Derives a standards-aligned, printable assessment from a generated lesson.
 * Every question traces back to a specific lesson section.
 */

import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, ClipboardList, Printer, CheckCircle2, AlertCircle,
  RotateCcw, XCircle, Save, BookOpen, Layers, GraduationCap,
  BookMarked, Eye, EyeOff, RefreshCw, Sparkles, School, RefreshCcw,
  AlertTriangle, Pencil, X as XIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useResources, type SavedResource } from './resources-context';
import type { GeneratedLesson } from './lesson-engine';
import {
  defaultAssessmentConfig,
  type AssessmentConfig,
  type AssessmentQuestion,
  type GeneratedAssessment,
  type QuestionKind,
  type BloomsLevel,
  type AssessmentVersion,
} from './assessment-engine';
import { generateAssessmentAI, SubscriptionRequiredError } from './lesson-api';
import { runCertification, type CertificationReport } from './certification';
import { CertificationGate } from './CertificationGate';
import { BrandingPanel } from './BrandingPanel';
import { BrandingHeaderRow } from './BrandingHeaderRow';
import { defaultBranding, hasBranding, type BrandingData } from './branding';
import { lessonContentKey, extractBlueprint, validateBlueprintCompleteness } from './lesson-blueprint';
import { GenerationProgress } from './GenerationProgress';
import { REGISTRY_VERSION } from './rendering-registry';
import { applyPrintBypass } from './export-watermark';

// ── Toast ─────────────────────────────────────────────────────────────────────
interface Toast { id: number; message: string; type: 'success' | 'error' }
function useToasts() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counter = useRef(0);
  function show(message: string, type: Toast['type'] = 'success') {
    const id = ++counter.current;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
  }
  return { toasts, show };
}
function ToastContainer({ toasts }: { toasts: Toast[] }) {
  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] flex flex-col gap-2 pointer-events-none print:hidden">
      <AnimatePresence>
        {toasts.map(t => (
          <motion.div key={t.id}
            initial={{ opacity: 0, y: 16, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className={`flex items-center gap-2.5 px-5 py-3 rounded-2xl shadow-xl text-sm font-semibold text-white ${t.type === 'success' ? 'bg-slate-900' : 'bg-red-600'}`}>
            {t.type === 'success' ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
            {t.message}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

// ── Config option helpers ─────────────────────────────────────────────────────
const QUESTION_TYPE_OPTIONS: { value: QuestionKind; label: string }[] = [
  { value: 'multiple-choice',       label: 'Multiple Choice' },
  { value: 'short-answer',          label: 'Short Answer' },
  { value: 'constructed-response',  label: 'Constructed Response' },
  { value: 'true-false',            label: 'True / False' },
  { value: 'fill-in-the-blank',     label: 'Fill in the Blank' },
  { value: 'matching',              label: 'Matching' },
  { value: 'performance-task',      label: 'Performance Task' },
];

const BLOOMS_OPTIONS: BloomsLevel[] = ['Remember', 'Understand', 'Apply', 'Analyze', 'Evaluate', 'Create'];
const BLOOMS_DESC: Record<BloomsLevel, string> = {
  Remember: 'Recall facts',
  Understand: 'Explain concepts',
  Apply: 'Use in new situations',
  Analyze: 'Break down and examine',
  Evaluate: 'Justify and critique',
  Create: 'Design and produce',
};

const LAYOUT_OPTIONS = [
  { value: 'student-friendly' as const, label: 'Student Friendly', desc: 'Large font, generous spacing' },
  { value: 'testing-format' as const,   label: 'Testing Format',   desc: 'Formal, numbered sections' },
  { value: 'state-test-style' as const, label: 'State-Test Style', desc: 'Mirrors standardized tests' },
  { value: 'worksheet-style' as const,  label: 'Worksheet Style',  desc: 'Compact, two-column' },
];

// ── Inline question edit form ─────────────────────────────────────────────────

function QuestionEditForm({ q, onSave, onCancel }: {
  q: AssessmentQuestion;
  onSave: (updated: AssessmentQuestion) => void;
  onCancel: () => void;
}) {
  const [stem, setStem] = useState(q.stem);
  const [choices, setChoices] = useState<string[]>(q.choices ?? []);
  const [answer, setAnswer] = useState(q.answer);
  const [explanation, setExplanation] = useState(q.explanation ?? '');
  const [matchingPairs, setMatchingPairs] = useState(
    q.matchingPairs ? q.matchingPairs.map(p => ({ ...p })) : []
  );

  function commit() {
    onSave({
      ...q,
      stem: stem.trim(),
      choices: choices.length ? choices : undefined,
      matchingPairs: matchingPairs.length ? matchingPairs : undefined,
      answer,
      explanation,
    });
  }

  const isMC   = q.kind === 'multiple-choice' || q.kind === 'true-false';
  const isOpen = q.kind === 'short-answer' || q.kind === 'constructed-response' || q.kind === 'performance-task';

  return (
    <div className="mt-3 ml-9 bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3 print:hidden">
      <p className="text-[11px] font-semibold text-blue-700 uppercase tracking-widest">Edit Question</p>

      {/* Stem */}
      <div>
        <label className="text-xs font-semibold text-slate-600 block mb-1">Question stem</label>
        <textarea value={stem} onChange={e => setStem(e.target.value)} rows={3}
          className="w-full text-sm border border-slate-200 rounded-lg p-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-300" />
      </div>

      {/* Choices (MC / TF) */}
      {isMC && (
        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1.5">
            Answer choices — <span className="text-blue-600">click the bullet to mark correct</span>
          </label>
          {choices.map((c, i) => {
            const letter = String.fromCharCode(65 + i);
            const isCorrect = answer === letter || answer === c;
            return (
              <div key={i} className="flex items-center gap-2 mb-1.5">
                <button type="button"
                  onClick={() => setAnswer(letter)}
                  className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all ${isCorrect ? 'border-emerald-500 bg-emerald-100' : 'border-slate-300 hover:border-emerald-400'}`}>
                  {isCorrect && <div className="w-2 h-2 rounded-full bg-emerald-500" />}
                </button>
                <span className="text-xs font-bold text-slate-500 w-5">{letter}.</span>
                <input value={c}
                  onChange={e => setChoices(ch => ch.map((v, j) => j === i ? e.target.value : v))}
                  className="flex-1 text-sm border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-300" />
              </div>
            );
          })}
        </div>
      )}

      {/* Matching pairs */}
      {q.kind === 'matching' && (
        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1.5">Matching pairs</label>
          {matchingPairs.map((pair, i) => (
            <div key={i} className="flex items-center gap-2 mb-1.5">
              <input value={pair.left} placeholder="Column A"
                onChange={e => setMatchingPairs(ps => ps.map((p, j) => j === i ? { ...p, left: e.target.value } : p))}
                className="flex-1 text-sm border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-300" />
              <span className="text-slate-400">→</span>
              <input value={pair.right} placeholder="Column B"
                onChange={e => setMatchingPairs(ps => ps.map((p, j) => j === i ? { ...p, right: e.target.value } : p))}
                className="flex-1 text-sm border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-300" />
            </div>
          ))}
        </div>
      )}

      {/* Answer for non-MC / non-matching */}
      {!isMC && q.kind !== 'matching' && (
        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1">
            {isOpen ? 'Sample answer / scoring guidance' : 'Correct answer'}
          </label>
          <textarea value={answer} onChange={e => setAnswer(e.target.value)} rows={isOpen ? 4 : 2}
            className="w-full text-sm border border-slate-200 rounded-lg p-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-300" />
        </div>
      )}

      {/* Explanation */}
      <div>
        <label className="text-xs font-semibold text-slate-600 block mb-1">Explanation for students (optional)</label>
        <textarea value={explanation} onChange={e => setExplanation(e.target.value)} rows={2}
          className="w-full text-sm border border-slate-200 rounded-lg p-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-300" />
      </div>

      <div className="flex gap-2 pt-1">
        <Button size="sm" onClick={commit}
          className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white gap-1.5">
          <CheckCircle2 size={13} /> Save changes
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}
          className="rounded-xl border-slate-200 text-slate-600 gap-1.5">
          <XIcon size={13} /> Cancel
        </Button>
      </div>
    </div>
  );
}

// ── Question renderer ─────────────────────────────────────────────────────────

function QuestionBlock({ q, index, showAnswer, layout, editing, onStartEdit, onSaveEdit, onCancelEdit }: {
  q: AssessmentQuestion;
  index: number;
  showAnswer: boolean;
  layout: AssessmentConfig['layout'];
  editing?: boolean;
  onStartEdit?: () => void;
  onSaveEdit?: (updated: AssessmentQuestion) => void;
  onCancelEdit?: () => void;
}) {
  const compact = layout === 'worksheet-style' || layout === 'state-test-style';
  const spacing = compact ? 'mb-5' : 'mb-8';
  const fontSize = layout === 'student-friendly' ? 'text-[15px]' : 'text-sm';

  return (
    <div className={`${spacing} break-inside-avoid`}>
      {/* Traceability tag + edit button — visible in preview, hidden in print */}
      <div className="print:hidden mb-1 flex items-center gap-2">
        {q.lessonSection && (
          <span className="text-[10px] font-medium text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full uppercase tracking-wide">
            From: {q.lessonSection.replace(/([A-Z])/g, ' $1').trim()}
            {showAnswer && <>&nbsp;·&nbsp;{q.bloomsLevel}</>}
          </span>
        )}
        {onStartEdit && !editing && (
          <button onClick={onStartEdit}
            className="text-[10px] font-semibold text-slate-400 hover:text-blue-600 flex items-center gap-1 transition-colors">
            <Pencil size={10} /> Edit
          </button>
        )}
      </div>

      {/* Question stem */}
      <div className={`flex gap-3 ${spacing === 'mb-8' ? 'mb-4' : 'mb-3'}`}>
        <span className={`font-bold text-slate-800 flex-shrink-0 ${fontSize} min-w-[1.5rem]`}>{index}.</span>
        <p className={`${fontSize} text-slate-800 leading-relaxed flex-1`}
          dangerouslySetInnerHTML={{ __html: q.stem.replace(/_____/g, '<span class="inline-block border-b-2 border-slate-800 w-24 mx-1 align-bottom">&nbsp;</span>') }} />
      </div>

      {/* Inline edit form */}
      {editing && onSaveEdit && onCancelEdit && (
        <QuestionEditForm q={q} onSave={onSaveEdit} onCancel={onCancelEdit} />
      )}

      {/* Multiple choice / True-False */}
      {!editing && (q.kind === 'multiple-choice' || q.kind === 'true-false') && q.choices && (
        <div className={`ml-9 space-y-${compact ? '1.5' : '2.5'}`}>
          {q.choices.map((choice, i) => {
            const letter = String.fromCharCode(65 + i);
            const isCorrect = showAnswer && (q.answer === letter || q.answer === choice);
            return (
              <div key={i} className={`flex items-start gap-2.5 ${isCorrect ? 'text-emerald-700 font-semibold' : 'text-slate-700'}`}>
                <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 mt-0.5 flex items-center justify-center ${isCorrect ? 'border-emerald-500 bg-emerald-100' : 'border-slate-400'}`}>
                  {isCorrect && <div className="w-2 h-2 rounded-full bg-emerald-500" />}
                </div>
                <span className={fontSize}><span className="font-semibold">{letter}.&nbsp;</span>{choice}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Fill in the blank */}
      {!editing && q.kind === 'fill-in-the-blank' && showAnswer && (
        <div className="ml-9 mt-2">
          <p className="text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1.5">
            Answer: {q.answer}
          </p>
        </div>
      )}

      {/* Matching */}
      {!editing && q.kind === 'matching' && q.matchingPairs && (
        <div className="ml-9 mt-2 grid grid-cols-2 gap-x-6 gap-y-2">
          <div className="space-y-2">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Column A</p>
            {q.matchingPairs.map((pair, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-xs font-bold text-slate-500 w-4">{i + 1}.</span>
                <span className={`${fontSize} text-slate-800`}>{pair.left}</span>
                {!showAnswer && <span className="flex-1 border-b border-dashed border-slate-400 h-4" />}
                {showAnswer && <span className="ml-2 text-xs font-semibold text-emerald-700">→ {pair.right}</span>}
              </div>
            ))}
          </div>
          {!showAnswer && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Column B</p>
              {[...q.matchingPairs].sort(() => Math.random() - 0.5).map((pair, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-500 w-4">{String.fromCharCode(65 + i)}.</span>
                  <span className={`${fontSize} text-slate-800`}>{pair.right}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Short answer / Constructed response / Performance task */}
      {!editing && (q.kind === 'short-answer' || q.kind === 'constructed-response' || q.kind === 'performance-task') && (
        <div className="ml-9 mt-2">
          {showAnswer ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
              <p className="text-xs font-semibold text-emerald-700 mb-1">Sample Answer / Scoring Guidance:</p>
              <p className={`${fontSize} text-emerald-800 whitespace-pre-wrap`}>{q.answer}</p>
              {q.explanation && <p className={`${fontSize} text-slate-500 mt-1 italic`}>{q.explanation}</p>}
            </div>
          ) : (
            <div className={`space-y-2 ${q.kind === 'performance-task' ? 'mt-2' : ''}`}>
              {Array.from({ length: q.kind === 'short-answer' ? 3 : q.kind === 'constructed-response' ? 5 : 8 }).map((_, i) => (
                <div key={i} className="border-b border-slate-300 h-6" />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Answer key — teacher copy, objective questions */}
      {!editing && showAnswer && q.kind !== 'short-answer' && q.kind !== 'constructed-response' && q.kind !== 'performance-task' && q.kind !== 'fill-in-the-blank' && q.kind !== 'matching' && (
        <div className="ml-9 mt-2 flex flex-wrap gap-3">
          <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1">
            ✓ {q.answer}
          </span>
          {q.commonMistake && (
            <span className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded-lg px-3 py-1">
              ⚠ Common mistake: {q.commonMistake}
            </span>
          )}
        </div>
      )}

      {/* Teacher tip */}
      {!editing && showAnswer && q.teacherTip && (
        <div className="ml-9 mt-1.5 print:hidden">
          <span className="text-xs text-blue-700 bg-blue-50 rounded-lg px-3 py-1 italic">
            💡 {q.teacherTip}
          </span>
        </div>
      )}

      {/* Points */}
      <div className="ml-9 mt-2">
        <span className="text-xs text-slate-400">{q.points === 1 ? '1 point' : `${q.points} points`}</span>
      </div>
    </div>
  );
}

// ── Assessment document ────────────────────────────────────────────────────────

function AssessmentDocument({ assessment, showAnswers, branding, editingId, onStartEdit, onSaveEdit, onCancelEdit }: {
  assessment: GeneratedAssessment;
  showAnswers: boolean;
  branding?: BrandingData;
  // Edit callbacks — omit to render in read-only mode (e.g. teacher copy print section)
  editingId?: string | null;
  onStartEdit?: (id: string) => void;
  onSaveEdit?: (id: string, updated: AssessmentQuestion) => void;
  onCancelEdit?: () => void;
}) {
  const { config } = assessment;
  const compact = config.layout === 'worksheet-style' || config.layout === 'state-test-style';

  return (
    <div className="bg-white">
      {/* School branding strip */}
      {branding && hasBranding(branding) && (
        <div className="mb-4">
          <BrandingHeaderRow branding={branding} />
        </div>
      )}

      {/* Header */}
      <div className={`${compact ? 'mb-6' : 'mb-8'} border-b-2 border-slate-800 pb-4`}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h1 className={`font-bold text-slate-900 leading-tight ${compact ? 'text-xl' : 'text-2xl'} mb-1`}>
              {assessment.title}
              {assessment.version !== 'A' && (
                <span className="ml-2 text-sm font-semibold text-slate-500 border border-slate-300 rounded px-1.5 py-0.5">
                  Version {assessment.version}
                </span>
              )}
              {showAnswers && (
                <span className="ml-2 text-sm font-semibold text-emerald-700 border border-emerald-300 bg-emerald-50 rounded px-1.5 py-0.5">
                  ANSWER KEY
                </span>
              )}
            </h1>
            <p className="text-sm text-slate-500">
              {assessment.gradeLabel} · {assessment.subject}
              {assessment.standard ? ` · ${assessment.standard}` : ''}
            </p>
          </div>
          <div className="text-right text-xs text-slate-400">
            <p>{assessment.totalPoints} pts total</p>
          </div>
        </div>

        {/* Header lines */}
        {(config.showNameLine || config.showDateLine || config.showTeacherLine || config.showPeriodLine) && (
          <div className={`mt-4 grid gap-x-8 gap-y-3 ${compact ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-2'}`}>
            {config.showNameLine && (
              <div className="flex items-end gap-2">
                <span className="text-sm font-semibold text-slate-700 flex-shrink-0">Name:</span>
                <div className="flex-1 border-b border-slate-400" />
              </div>
            )}
            {config.showDateLine && (
              <div className="flex items-end gap-2">
                <span className="text-sm font-semibold text-slate-700 flex-shrink-0">Date:</span>
                <div className="flex-1 border-b border-slate-400" />
              </div>
            )}
            {config.showTeacherLine && (
              <div className="flex items-end gap-2">
                <span className="text-sm font-semibold text-slate-700 flex-shrink-0">Teacher:</span>
                <div className="flex-1 border-b border-slate-400" />
              </div>
            )}
            {config.showPeriodLine && (
              <div className="flex items-end gap-2">
                <span className="text-sm font-semibold text-slate-700 flex-shrink-0">Period:</span>
                <div className="flex-1 border-b border-slate-400" />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Questions */}
      <div className="space-y-0">
        {assessment.questions.map((q, i) => (
          <QuestionBlock
            key={q.id}
            q={q}
            index={i + 1}
            showAnswer={showAnswers}
            layout={config.layout}
            editing={editingId === q.id}
            onStartEdit={onStartEdit ? () => onStartEdit(q.id) : undefined}
            onSaveEdit={onSaveEdit ? (updated) => onSaveEdit(q.id, updated) : undefined}
            onCancelEdit={onCancelEdit}
          />
        ))}
      </div>

      {/* Footer */}
      <div className="mt-8 pt-4 border-t border-slate-200 flex items-center justify-between text-xs text-slate-400">
        <span>TeachBud · {assessment.gradeLabel} · {assessment.subject}</span>
        <span>{assessment.totalPoints} points total</span>
      </div>
    </div>
  );
}

// ── Config view ────────────────────────────────────────────────────────────────

function ConfigView({
  lesson, config, setConfig, onGenerate, generating, onBack, error,
}: {
  lesson: GeneratedLesson;
  config: AssessmentConfig;
  setConfig: React.Dispatch<React.SetStateAction<AssessmentConfig>>;
  onGenerate: () => void;
  generating: boolean;
  onBack: () => void;
  error: string | null;
}) {
  function toggle<K extends keyof AssessmentConfig>(key: K, value: AssessmentConfig[K]) {
    setConfig(prev => ({ ...prev, [key]: value }));
  }
  function toggleType(kind: QuestionKind) {
    setConfig(prev => {
      const types = prev.questionTypes.includes(kind)
        ? prev.questionTypes.filter(t => t !== kind)
        : [...prev.questionTypes, kind];
      return { ...prev, questionTypes: types.length ? types : [kind] };
    });
  }

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      {/* Back */}
      <button onClick={onBack}
        className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors px-3 py-2 rounded-xl hover:bg-slate-100 print:hidden">
        <ChevronLeft size={15} /> Back to Lesson
      </button>

      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-600 to-teal-700 rounded-2xl p-5 text-white shadow-lg">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <ClipboardList size={20} />
          </div>
          <div>
            <h2 className="text-lg font-bold">Generate Assessment</h2>
            <p className="text-emerald-100 text-sm leading-snug mt-0.5">
              Creating from: <span className="font-semibold text-white">{lesson.title}</span>
            </p>
            <p className="text-emerald-200 text-xs mt-1">{lesson.gradeLabel} · {lesson.subject}</p>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-2.5 text-sm text-red-700">
          <AlertCircle size={15} className="flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Config card */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-6">

        {/* Question count + difficulty */}
        <div className="grid grid-cols-2 gap-5">
          <div>
            <p className="text-sm font-semibold text-slate-700 mb-2.5">Number of Questions</p>
            <div className="flex gap-2">
              {([5, 10, 15, 20] as const).map(n => (
                <button key={n} onClick={() => toggle('questionCount', n)}
                  className={`flex-1 h-9 rounded-xl text-sm font-semibold border transition-all ${config.questionCount === n ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                  {n}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-700 mb-2.5">Difficulty</p>
            <div className="space-y-1.5">
              {(['easier', 'same', 'harder'] as const).map(d => (
                <label key={d} className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border cursor-pointer transition-all ${config.difficulty === d ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`}>
                  <input type="radio" name="difficulty" value={d} checked={config.difficulty === d}
                    onChange={() => toggle('difficulty', d)} className="sr-only" />
                  <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center ${config.difficulty === d ? 'border-emerald-500' : 'border-slate-300'}`}>
                    {config.difficulty === d && <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}
                  </div>
                  <span className={`text-sm font-medium capitalize ${config.difficulty === d ? 'text-emerald-800' : 'text-slate-600'}`}>{d}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Question types */}
        <div>
          <p className="text-sm font-semibold text-slate-700 mb-2.5">Question Types</p>
          <div className="grid grid-cols-2 gap-2">
            {QUESTION_TYPE_OPTIONS.map(opt => {
              const checked = config.questionTypes.includes(opt.value);
              return (
                <label key={opt.value}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border cursor-pointer transition-all ${checked ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`}>
                  <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${checked ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300'}`}>
                    {checked && <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 12 12"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                  </div>
                  <span className={`text-sm font-medium ${checked ? 'text-emerald-800' : 'text-slate-600'}`} onClick={() => toggleType(opt.value)}>{opt.label}</span>
                </label>
              );
            })}
          </div>
        </div>

        {/* Bloom's + Version */}
        <div className="grid grid-cols-2 gap-5">
          <div>
            <p className="text-sm font-semibold text-slate-700 mb-2.5">Bloom's Taxonomy Level</p>
            <div className="space-y-1.5">
              {BLOOMS_OPTIONS.map(level => (
                <label key={level}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border cursor-pointer transition-all ${config.bloomsLevel === level ? 'border-violet-400 bg-violet-50' : 'border-slate-200 hover:border-slate-300'}`}>
                  <input type="radio" name="blooms" value={level} checked={config.bloomsLevel === level}
                    onChange={() => toggle('bloomsLevel', level)} className="sr-only" />
                  <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center ${config.bloomsLevel === level ? 'border-violet-500' : 'border-slate-300'}`}>
                    {config.bloomsLevel === level && <div className="w-1.5 h-1.5 rounded-full bg-violet-500" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className={`text-sm font-medium ${config.bloomsLevel === level ? 'text-violet-800' : 'text-slate-600'}`}>{level}</span>
                    <span className="block text-xs text-slate-400">{BLOOMS_DESC[level]}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-700 mb-2.5">Assessment Version</p>
            {(['A', 'B', 'C'] as AssessmentVersion[]).map(v => (
              <label key={v}
                className={`flex items-start gap-2.5 px-3 py-2.5 rounded-xl border cursor-pointer transition-all mb-2 ${config.version === v ? 'border-blue-400 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}>
                <input type="radio" name="version" value={v} checked={config.version === v}
                  onChange={() => toggle('version', v)} className="sr-only" />
                <div className={`mt-0.5 w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${config.version === v ? 'border-blue-500' : 'border-slate-300'}`}>
                  {config.version === v && <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
                </div>
                <div>
                  <span className={`text-sm font-semibold ${config.version === v ? 'text-blue-800' : 'text-slate-700'}`}>Version {v}</span>
                  <span className="block text-xs text-slate-400">
                    {v === 'A' ? 'Same numbers as lesson' : v === 'B' ? 'Different numbers, same skill' : 'New context, same skill'}
                  </span>
                </div>
              </label>
            ))}

            {/* Answer key options */}
            <p className="text-sm font-semibold text-slate-700 mb-2.5 mt-4">Answer Key</p>
            {[
              { key: 'includeAnswerKey' as const, label: 'Include in student copy' },
              { key: 'separateTeacherCopy' as const, label: 'Separate teacher copy' },
            ].map(opt => (
              <label key={opt.key}
                className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border cursor-pointer transition-all mb-2 ${config[opt.key] ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`}>
                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${config[opt.key] ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300'}`}
                  onClick={() => toggle(opt.key, !config[opt.key])}>
                  {config[opt.key] && <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 12 12"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                </div>
                <span className={`text-sm font-medium ${config[opt.key] ? 'text-emerald-800' : 'text-slate-600'}`}>{opt.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Layout */}
        <div>
          <p className="text-sm font-semibold text-slate-700 mb-2.5">Layout Style</p>
          <div className="grid grid-cols-2 gap-2">
            {LAYOUT_OPTIONS.map(opt => (
              <label key={opt.value}
                className={`flex items-start gap-2.5 px-3 py-2.5 rounded-xl border cursor-pointer transition-all ${config.layout === opt.value ? 'border-blue-400 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}>
                <input type="radio" name="layout" value={opt.value} checked={config.layout === opt.value}
                  onChange={() => toggle('layout', opt.value)} className="sr-only" />
                <div className={`mt-0.5 w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${config.layout === opt.value ? 'border-blue-500' : 'border-slate-300'}`}>
                  {config.layout === opt.value && <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
                </div>
                <div>
                  <span className={`text-sm font-semibold ${config.layout === opt.value ? 'text-blue-800' : 'text-slate-700'}`}>{opt.label}</span>
                  <span className="block text-xs text-slate-400">{opt.desc}</span>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Header fields */}
        <div>
          <p className="text-sm font-semibold text-slate-700 mb-2.5">Header Fields</p>
          <div className="grid grid-cols-2 gap-2">
            {([
              { key: 'showNameLine' as const,    label: 'Name ___________' },
              { key: 'showDateLine' as const,    label: 'Date ___________' },
              { key: 'showTeacherLine' as const, label: 'Teacher ________' },
              { key: 'showPeriodLine' as const,  label: 'Period __________' },
            ]).map(opt => (
              <label key={opt.key}
                className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border cursor-pointer transition-all ${config[opt.key] ? 'border-slate-400 bg-slate-50' : 'border-slate-200 hover:border-slate-300'}`}>
                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${config[opt.key] ? 'border-slate-500 bg-slate-500' : 'border-slate-300'}`}
                  onClick={() => toggle(opt.key, !config[opt.key])}>
                  {config[opt.key] && <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 12 12"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                </div>
                <span className={`text-sm font-mono ${config[opt.key] ? 'text-slate-800' : 'text-slate-500'}`}>{opt.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Generate button */}
      <Button onClick={onGenerate} disabled={generating}
        className="w-full h-12 rounded-xl text-base font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm">
        {generating ? (
          <span className="flex items-center gap-2">
            <svg className="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Generating Assessment…
          </span>
        ) : (
          <span className="flex items-center gap-2"><Sparkles size={17} /> Generate Assessment</span>
        )}
      </Button>
    </div>
  );
}

// ── Result view ────────────────────────────────────────────────────────────────

function ResultView({
  assessment, lesson, onBack, onNewAssessment, onSave, savedResourceId,
  branding, showBrandingPanel, onToggleBrandingPanel, onBrandingChange, outOfSync,
  onQuestionEdit,
}: {
  assessment: GeneratedAssessment;
  lesson: GeneratedLesson;
  onBack: () => void;
  onNewAssessment: () => void;
  onSave: () => void;
  savedResourceId: string | null;
  branding: BrandingData;
  showBrandingPanel: boolean;
  onToggleBrandingPanel: () => void;
  onBrandingChange: (b: BrandingData) => void;
  outOfSync: boolean;
  onQuestionEdit: (id: string, updated: AssessmentQuestion) => void;
}) {
  const [showTeacherCopy, setShowTeacherCopy] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [certReport, setCertReport] = useState<CertificationReport | null>(null);
  const [pendingExportAction, setPendingExportAction] = useState<(() => void) | null>(null);

  const separateTeacherCopy = assessment.config.separateTeacherCopy;

  function doPrint(isBypass = false) {
    applyPrintBypass(() => window.print(), isBypass);
  }

  function handlePrint() {
    const blueprint = lesson.blueprint ?? extractBlueprint(lesson);
    const report = runCertification({
      resource: 'assessment',
      assessment,
      lesson,
      blueprint,
    });
    if (!report.passed || report.hasWarnings) {
      setCertReport(report);
      setPendingExportAction(() => () => doPrint(true)); // bypass — watermark applied
      return;
    }
    doPrint();
  }

  return (
    <>
      {certReport && (
        <CertificationGate
          report={certReport}
          resourceLabel="Assessment"
          onExportAnyway={() => {
            const action = pendingExportAction;
            setCertReport(null);
            setPendingExportAction(null);
            action?.();
          }}
          onClose={() => {
            setCertReport(null);
            setPendingExportAction(null);
          }}
        />
      )}
      {/* Top bar */}
      <div className="flex flex-wrap items-center gap-2 mb-5 print:hidden">
        <button onClick={onBack}
          className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors px-3 py-2 rounded-xl hover:bg-slate-100">
          <ChevronLeft size={15} /> Back to Lesson
        </button>
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={onToggleBrandingPanel}
          className={`rounded-xl gap-1.5 transition-all ${showBrandingPanel ? 'bg-slate-900 text-white border-slate-900 hover:bg-slate-800' : 'border-slate-200 hover:border-slate-300 text-slate-600'}`}>
          <School size={14} /> Branding
        </Button>
        <Button variant="outline" size="sm" onClick={() => { setShowTeacherCopy(v => !v); setEditingId(null); }}
          className={`rounded-xl gap-1.5 ${showTeacherCopy ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-600'}`}>
          {showTeacherCopy ? <EyeOff size={14} /> : <Eye size={14} />}
          {showTeacherCopy ? 'Hide Answer Key' : 'Show Answer Key'}
        </Button>
        {/* Print button — label changes based on separateTeacherCopy setting */}
        <Button variant="outline" size="sm" onClick={handlePrint}
          className="rounded-xl border-slate-200 hover:border-slate-300 text-slate-600 gap-1.5">
          <Printer size={14} />
          {separateTeacherCopy ? 'Print Both Copies' : 'Print / PDF'}
        </Button>
        <Button variant="outline" size="sm" onClick={onSave}
          className={`rounded-xl gap-1.5 ${savedResourceId ? 'border-green-200 bg-green-50 text-green-700' : 'border-slate-200 text-slate-600'}`}>
          {savedResourceId ? <CheckCircle2 size={15} /> : <Save size={15} />}
          {savedResourceId ? 'Saved' : 'Save'}
        </Button>
        <Button variant="outline" size="sm" onClick={onNewAssessment}
          className="rounded-xl border-slate-200 text-slate-600 gap-1.5">
          <RotateCcw size={14} /> New Assessment
        </Button>
      </div>

      {/* Branding panel */}
      {showBrandingPanel && (
        <div className="mb-4 print:hidden">
          <BrandingPanel
            branding={branding}
            onChange={onBrandingChange}
            open={showBrandingPanel}
            onToggle={onToggleBrandingPanel}
          />
        </div>
      )}

      {/* Sync-out-of-date warning */}
      {outOfSync && (
        <div className="mb-4 flex items-center gap-2.5 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800 print:hidden">
          <AlertTriangle size={15} className="flex-shrink-0 text-amber-500" />
          <span className="flex-1">
            <strong>Your lesson has changed</strong> since this assessment was generated. The questions may no longer match the current lesson content.
          </span>
          <Button variant="outline" size="sm" onClick={onNewAssessment}
            className="rounded-xl border-amber-300 text-amber-800 hover:bg-amber-100 gap-1.5 flex-shrink-0">
            <RefreshCcw size={13} /> Regenerate
          </Button>
        </div>
      )}

      {/* Version badges */}
      <div className="flex items-center gap-2 mb-4 print:hidden">
        <span className="text-xs font-medium text-slate-500">Assessment Version {assessment.version}</span>
        <span className="w-1 h-1 rounded-full bg-slate-300" />
        <span className="text-xs text-slate-400">{assessment.questions.length} questions · {assessment.totalPoints} points</span>
        {showTeacherCopy && <><span className="w-1 h-1 rounded-full bg-slate-300" /><span className="text-xs font-semibold text-emerald-600">Teacher Copy</span></>}
        {separateTeacherCopy && !showTeacherCopy && (
          <><span className="w-1 h-1 rounded-full bg-slate-300" />
          <span className="text-xs text-slate-400">Separate teacher copy included when printing</span></>
        )}
      </div>

      {/*
        ── Print architecture ────────────────────────────────────────────────
        Both student and teacher copies live in the DOM at all times.
        Screen visibility is toggled via `screen-hidden` (a class that only
        hides in @media screen). In @media print:
          • .assessment-student-copy always prints
          • .assessment-teacher-copy prints only when separateTeacherCopy is
            true (controlled by the `will-print-teacher` class)
        This gives teachers one-click printing of both copies with a real
        page break between them, without any JS tricks.
        ─────────────────────────────────────────────────────────────────── */}

      {/* Student copy — screen: visible when !showTeacherCopy; print: always */}
      <div className={`assessment-student-copy bg-white rounded-2xl border border-slate-100 shadow-sm p-8 ${showTeacherCopy ? 'screen-hidden' : ''}`}>
        <AssessmentDocument
          assessment={assessment}
          showAnswers={false}
          branding={branding}
          editingId={!showTeacherCopy ? editingId : null}
          onStartEdit={!showTeacherCopy ? (id) => setEditingId(id) : undefined}
          onSaveEdit={!showTeacherCopy ? (id, updated) => { onQuestionEdit(id, updated); setEditingId(null); } : undefined}
          onCancelEdit={!showTeacherCopy ? () => setEditingId(null) : undefined}
        />
      </div>

      {/* Teacher copy — screen: visible when showTeacherCopy; print: when separateTeacherCopy */}
      <div className={`assessment-teacher-copy bg-white rounded-2xl border border-emerald-100 shadow-sm p-8 mt-6 ${separateTeacherCopy ? 'will-print-teacher' : ''} ${showTeacherCopy ? '' : 'screen-hidden'}`}>
        {/* Screen-only label */}
        <div className="print:hidden mb-4 flex items-center gap-2">
          <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
            TEACHER COPY — Answer Key
          </span>
        </div>
        <AssessmentDocument
          assessment={assessment}
          showAnswers={true}
          branding={branding}
          editingId={showTeacherCopy ? editingId : null}
          onStartEdit={showTeacherCopy ? (id) => setEditingId(id) : undefined}
          onSaveEdit={showTeacherCopy ? (id, updated) => { onQuestionEdit(id, updated); setEditingId(null); } : undefined}
          onCancelEdit={showTeacherCopy ? () => setEditingId(null) : undefined}
        />
      </div>

      {/* Print styles ─────────────────────────────────────────────────────────
          @media screen: screen-hidden hides an element without affecting print
          @media print:  both copies render; teacher copy gets a page break when
                         the `will-print-teacher` class is present
      */}
      <style>{`
        @page { margin: 18mm 15mm; }
        @media screen { .screen-hidden { display: none !important; } }
        @media print {
          /*
           * DO NOT use "body > * { display:none }" — in a React app the direct
           * child of body is #root, which traps .assessment-student-copy inside
           * a hidden ancestor where display:block overrides cannot reach it.
           *
           * Instead: show both copies normally; hide the teacher copy unless
           * will-print-teacher is set; let print:hidden classes handle the rest.
           */
          .assessment-student-copy {
            display: block !important;
            box-shadow: none !important;
            border: 0 !important;
            border-radius: 0 !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          /* Teacher copy: hidden unless the teacher asked for a separate copy */
          .assessment-teacher-copy { display: none !important; }
          .will-print-teacher {
            display: block !important;
            box-shadow: none !important;
            border: 0 !important;
            border-radius: 0 !important;
            padding: 0 !important;
            margin: 0 !important;
            page-break-before: always;
            break-before: page;
          }
          .print\\:hidden, .screen-hidden { display: none !important; }
        }
      `}</style>
    </>
  );
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface AssessmentGeneratorProps {
  onBack: () => void;
  onGoMyResources: () => void;
  onNavigateTo: (section: string, ctx?: { fromLessonTitle?: string; fromLessonId?: string }) => void;
  lessonResource?: SavedResource | null;
  initialResource?: SavedResource | null;
}

// ── Main component ────────────────────────────────────────────────────────────

export function AssessmentGenerator({
  onBack, onGoMyResources, lessonResource, initialResource,
}: AssessmentGeneratorProps) {
  const { saveResource, updateResource } = useResources();
  const { toasts, show: showToast } = useToasts();

  const lesson: GeneratedLesson | null = React.useMemo(() => {
    try {
      if (!lessonResource?.lessonData) return null;
      const parsed = JSON.parse(lessonResource.lessonData) as GeneratedLesson;
      // Re-extract blueprint for AI lessons saved before blueprint extraction existed.
      // Without this, the Assessment server prompt receives no blueprint items and
      // generates questions unanchored to the lesson's actual problems.
      if (!parsed.blueprint && parsed.spec) {
        parsed.blueprint = extractBlueprint(parsed);
      }
      return parsed;
    } catch { return null; }
  }, [lessonResource]);

  const savedAssessment: GeneratedAssessment | null = React.useMemo(() => {
    try {
      if (initialResource?.lessonData) return JSON.parse(initialResource.lessonData) as GeneratedAssessment;
      return null;
    } catch { return null; }
  }, [initialResource]);

  const [view, setView] = useState<'config' | 'loading' | 'result'>(savedAssessment ? 'result' : 'config');
  const [config, setConfig] = useState<AssessmentConfig>(() =>
    savedAssessment ? savedAssessment.config : defaultAssessmentConfig()
  );
  const [assessment, setAssessment] = useState<GeneratedAssessment | null>(savedAssessment);
  // Inline edits keyed by question id — persists through Save
  const [editedQuestions, setEditedQuestions] = useState<Record<string, AssessmentQuestion>>(() => {
    // Restore any edits that were already saved inside the resource data
    return {};
  });
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedResourceId, setSavedResourceId] = useState<string | null>(initialResource?.id ?? null);
  const [branding, setBranding] = useState<BrandingData>(() => {
    if (savedAssessment?.branding) return savedAssessment.branding as unknown as BrandingData;
    return defaultBranding();
  });
  const [showBrandingPanel, setShowBrandingPanel] = useState(false);

  // Merge any inline edits on top of the AI-generated questions
  const effectiveAssessment = React.useMemo((): GeneratedAssessment | null => {
    if (!assessment) return null;
    if (Object.keys(editedQuestions).length === 0) return assessment;
    const mergedQuestions = assessment.questions.map(q => editedQuestions[q.id] ?? q);
    const totalPoints = mergedQuestions.reduce((s, q) => s + q.points, 0);
    return { ...assessment, questions: mergedQuestions, totalPoints };
  }, [assessment, editedQuestions]);

  // Detect when the source lesson has changed since the assessment was generated
  const outOfSync = React.useMemo(() => {
    if (!assessment?.syncKey || !lesson) return false;
    return assessment.syncKey !== lessonContentKey(lesson);
  }, [assessment, lesson]);

  function handleQuestionEdit(id: string, updated: AssessmentQuestion) {
    setEditedQuestions(prev => ({ ...prev, [id]: updated }));
    setSavedResourceId(prev => prev); // keep saved state; user must re-Save to persist
  }

  const generationIdRef = React.useRef(0);

  function handleCancelGeneration() {
    generationIdRef.current++;
    setGenerating(false);
    setView('config');
  }

  async function handleGenerate() {
    if (!lesson) return;

    // ── Blueprint Completeness Gate ───────────────────────────────────────────
    // Every generator obeys the same contract: refuse to render from an incomplete
    // blueprint. An assessment built on sparse blueprint items produces questions
    // unanchored to what the teacher actually taught.
    const bpForCheck = lesson.blueprint ?? extractBlueprint(lesson);
    const completeness = validateBlueprintCompleteness(bpForCheck);
    if (!completeness.complete) {
      const missingList = completeness.missingFields
        .map(f => `${f} (${completeness.reasons[f]})`)
        .join('; ');
      setError(
        `This lesson's blueprint is incomplete and cannot produce an assessment. ` +
        `Missing content: ${missingList}. ` +
        `Please return to the lesson and regenerate it — the system will rebuild the blueprint with all required content.`
      );
      return;
    }

    const myGenId = ++generationIdRef.current;
    setGenerating(true);
    setView('loading');
    setError(null);
    try {
      const result = await generateAssessmentAI(lesson, config);
      if (generationIdRef.current !== myGenId) return;
      setAssessment(result);
      setEditedQuestions({});
      setSavedResourceId(null);
      setView('result');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      if (generationIdRef.current !== myGenId) return;
      if (err instanceof SubscriptionRequiredError) {
        // 402 from server — show the human-readable message and let the teacher navigate to Upgrade
        showToast(err.message, 'error');
        setView('config');
      } else {
        setError(err instanceof Error ? err.message : 'Generation failed. Please try again.');
        setView('config');
      }
    } finally {
      setGenerating(false);
    }
  }

  function buildAssessmentData(): GeneratedAssessment {
    if (!effectiveAssessment) return assessment!;
    return { ...effectiveAssessment, branding: branding as unknown as Record<string, unknown> };
  }

  function handleSave() {
    if (!effectiveAssessment) return;
    const payload = buildAssessmentData();
    const resourceFields = {
      type: 'assessment' as const,
      title: payload.title,
      grade: payload.grade,
      subject: payload.subject,
      topic: lesson?.topic ?? '',
      standard: payload.standard,
      plainText: payload.questions.map((q, i) => `${i + 1}. ${q.stem}`).join('\n'),
      lessonData: JSON.stringify(payload),
      lastEditedAt: new Date().toISOString(),
      lessonId: lessonResource?.id,
      renderingSpecVersion: REGISTRY_VERSION,
    };
    if (savedResourceId) {
      updateResource(savedResourceId, resourceFields);
      showToast('Assessment updated.');
    } else {
      const id = saveResource(resourceFields);
      setSavedResourceId(id);
      showToast('Assessment saved to My Resources.');
    }
  }

  // No lesson state
  if (!lesson) {
    return (
      <div className="max-w-2xl mx-auto flex flex-col items-center justify-center min-h-[50vh] text-center gap-5 py-12">
        <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center">
          <ClipboardList size={30} className="text-emerald-600" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-slate-800 mb-2">No lesson selected</h3>
          <p className="text-slate-500 text-sm max-w-sm mx-auto">
            Generate a lesson in the Lesson Planner first, then click <strong>Create Assessment</strong> from the lesson view.
          </p>
        </div>
        <button onClick={onBack}
          className="text-sm font-semibold text-primary hover:text-primary/80 transition-colors flex items-center gap-1">
          <ChevronLeft size={14} /> Go to Lesson Planner
        </button>
      </div>
    );
  }

  return (
    <>
      <ToastContainer toasts={toasts} />
      {view === 'loading' ? (
        <GenerationProgress resourceType="assessment" onCancel={handleCancelGeneration} />
      ) : view === 'config' ? (
        <motion.div key="config" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}>
          <ConfigView
            lesson={lesson}
            config={config}
            setConfig={setConfig}
            onGenerate={handleGenerate}
            generating={generating}
            onBack={onBack}
            error={error}
          />
        </motion.div>
      ) : assessment ? (
        <motion.div key="result" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl mx-auto">
          <ResultView
            assessment={effectiveAssessment!}
            lesson={lesson}
            onBack={onBack}
            onNewAssessment={() => { setView('config'); setAssessment(null); setEditedQuestions({}); setSavedResourceId(null); window.scrollTo({ top: 0 }); }}
            onSave={handleSave}
            savedResourceId={savedResourceId}
            branding={branding}
            showBrandingPanel={showBrandingPanel}
            onToggleBrandingPanel={() => setShowBrandingPanel(v => !v)}
            onBrandingChange={setBranding}
            outOfSync={outOfSync}
            onQuestionEdit={handleQuestionEdit}
          />
        </motion.div>
      ) : null}
    </>
  );
}
